import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../api';
import { IAttachmentsApi } from '../api/contracts';
import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_DESCRIPTION_MAX,
  ATTACHMENT_MAX_MB,
  ATTACHMENT_PAGE_SIZE,
  attachmentDescriptionError,
  attachmentFileError,
  attachmentKind,
  attachmentRenameError,
  attachmentUpdateErrorMessage,
  attachmentUploadErrorMessage,
  formatFileSize,
  splitFilename,
} from '../utils/attachment';
import { canManageAttachments, canUploadAttachments, canViewAttachments } from '../utils/permissions';
import { inputClass, labelClass, primaryButtonClass, rowActionClass, rowDangerActionClass, secondaryButtonClass } from './formStyles';
import { showToast } from './Toast';

/** Query-string key for this section's page (namespaced so it never clashes with the page's own params). */
export const ATTACHMENTS_PAGE_PARAM = 'attachmentsPage';
/** How long a download's object URL is kept before it is revoked (the browser has copied it by then). */
const DOWNLOAD_URL_TTL_MS = 60_000;

const PARENT_NOUNS: Record<types.AttachmentParentType, string> = {
  CUSTOMER: 'customer',
  PROPERTY: 'property',
  JOB: 'job',
};

const KIND_STYLES: Record<string, string> = {
  IMG: 'bg-emerald-100 text-emerald-800',
  PDF: 'bg-red-100 text-red-800',
  DOC: 'bg-blue-100 text-blue-800',
  XLS: 'bg-green-100 text-green-800',
  TXT: 'bg-gray-100 text-gray-700',
  CSV: 'bg-amber-100 text-amber-800',
  FILE: 'bg-gray-100 text-gray-700',
};

interface UploadEntry {
  key: string;
  name: string;
  size: number;
  /** 0..1 */
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

interface EditState {
  id: string;
  base: string;
  extension: string;
  description: string;
}

interface PreviewState {
  attachment: types.Attachment;
  url: string | null;
}

export interface AttachmentsSectionProps {
  parentType: types.AttachmentParentType;
  parentId: string;
  user: types.User | null | undefined;
  /** Section container classes (defaults to the customer / property card style). */
  sectionClassName?: string;
  headingClassName?: string;
}

/** Created / uploaded time, short and locale-aware. */
function formatAttachmentDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function createObjectUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

function revokeObjectUrl(url: string): void {
  try { URL.revokeObjectURL(url); } catch { /* already gone */ }
}

/** Revoke an object URL this section created and stop tracking it. */
function releaseTrackedUrl(tracked: Set<string>, url: string | null | undefined): void {
  if (!url) return;
  revokeObjectUrl(url);
  tracked.delete(url);
}

/**
 * Attachments (documents and photos) on a customer, property or job
 * (tracker item 47). ADMIN / OFFICE_MANAGER / TECHNICIAN list, preview,
 * download and upload; ADMIN / OFFICE_MANAGER also rename, edit the
 * description and delete. Everyone else sees nothing. Downloads need the
 * Bearer header, so files are fetched as Blobs and shown through object
 * URLs (revoked when closed and on unmount); the filename comes from the
 * metadata, never from Content-Disposition.
 */
export function AttachmentsSection({
  parentType,
  parentId,
  user,
  sectionClassName = 'bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6',
  headingClassName = 'text-sm font-semibold text-gray-900 uppercase tracking-wide',
}: AttachmentsSectionProps) {
  const api = useMemo<IAttachmentsApi>(() => createAPIClient(), []);
  const canView = canViewAttachments(user);
  const canUpload = canUploadAttachments(user);
  const canManage = canManageAttachments(user);
  const noun = PARENT_NOUNS[parentType];
  const idBase = `attachments-${parentType.toLowerCase()}`;

  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Math.floor(Number(params.get(ATTACHMENTS_PAGE_PARAM))) || 1);
  const [attachments, setAttachments] = useState<types.Attachment[] | null>(null);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [editing, setEditing] = useState<EditState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const mounted = useRef(true);
  const loadSeq = useRef(0);
  const previewSeq = useRef(0);
  const uploadSeq = useRef(0);
  const objectUrls = useRef(new Set<string>());
  const urlTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const uploadAbort = useRef<AbortController | null>(null);

  // Revoke every object URL and cancel uploads when the section goes away
  useEffect(() => {
    mounted.current = true;
    const urls = objectUrls.current;
    const timers = urlTimers.current;
    return () => {
      mounted.current = false;
      uploadAbort.current?.abort();
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      urls.forEach(revokeObjectUrl);
      urls.clear();
    };
  }, []);

  const trackUrl = (blob: Blob): string => {
    const url = createObjectUrl(blob);
    objectUrls.current.add(url);
    return url;
  };
  const releaseUrl = (url: string | null | undefined) => releaseTrackedUrl(objectUrls.current, url);

  const setPage = useCallback((next: number) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      if (next <= 1) updated.delete(ATTACHMENTS_PAGE_PARAM); else updated.set(ATTACHMENTS_PAGE_PARAM, String(next));
      return updated;
    });
  }, [setParams]);

  useEffect(() => {
    if (!canView) return;
    const seq = ++loadSeq.current;
    setLoadError(null);
    api.listAttachments(parentType, parentId, page, ATTACHMENT_PAGE_SIZE)
      .then((result) => {
        if (seq !== loadSeq.current || !mounted.current) return;
        const pages = Math.max(1, result.pagination.pages);
        // The page in the URL no longer exists (deletes elsewhere, or a stale link): go to the last one
        if (result.data.length === 0 && page > 1 && page > pages) {
          setPage(pages);
          return;
        }
        setAttachments(result.data);
        setPagination(result.pagination);
      })
      .catch((err) => {
        if (seq !== loadSeq.current || !mounted.current) return;
        setAttachments(null);
        setLoadError(getErrorMessage(err, 'Could not load attachments'));
      });
  }, [api, canView, parentType, parentId, page, reloadKey, setPage]);

  const closePreview = useCallback(() => {
    previewSeq.current += 1;
    const tracked = objectUrls.current;
    setPreview((current) => {
      releaseTrackedUrl(tracked, current?.url);
      return null;
    });
  }, []);

  // Escape closes the preview
  useEffect(() => {
    if (!preview) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, closePreview]);

  if (!canView) return null;

  const reload = () => setReloadKey((key) => key + 1);

  /** A 404 means it was deleted (or its parent was) elsewhere: show the current list. */
  const reloadIfGone = (err: unknown) => {
    if (getErrorCode(err) === 'NOT_FOUND') reload();
  };

  const patchUpload = (key: string, patch: Partial<UploadEntry>) => {
    if (!mounted.current) return;
    setUploads((current) => current.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)));
  };

  const startUploads = async (files: File[]) => {
    if (files.length === 0 || uploading) return;
    const descriptionIssue = attachmentDescriptionError(uploadDescription);
    if (descriptionIssue) { showToast('error', descriptionIssue); return; }

    const entries = files.map((file) => {
      const issue = attachmentFileError(file);
      const entry: UploadEntry = {
        key: `upload-${++uploadSeq.current}`,
        name: file.name,
        size: file.size,
        progress: 0,
        status: issue ? 'error' : 'pending',
        error: issue ?? undefined,
      };
      return { file, entry };
    });
    // Finished entries from an earlier batch make room for the new ones; failures stay until dismissed
    setUploads((current) => [...current.filter((entry) => entry.status === 'error'), ...entries.map(({ entry }) => entry)]);
    entries.filter(({ entry }) => entry.error).forEach(({ entry }) => showToast('error', entry.error!));

    const valid = entries.filter(({ entry }) => !entry.error);
    if (valid.length === 0) return;

    setUploading(true);
    const controller = new AbortController();
    uploadAbort.current = controller;
    const description = uploadDescription.trim();
    let succeeded = 0;
    let lastUploaded = '';
    // One request per file, one at a time (kinder to phone connections)
    for (const { file, entry } of valid) {
      if (controller.signal.aborted) break;
      patchUpload(entry.key, { status: 'uploading' });
      try {
        await api.uploadAttachment(parentType, parentId, file, {
          description: description || undefined,
          signal: controller.signal,
          onProgress: (fraction) => patchUpload(entry.key, { progress: fraction }),
        });
        succeeded += 1;
        lastUploaded = file.name;
        patchUpload(entry.key, { status: 'done', progress: 1 });
      } catch (err) {
        if (controller.signal.aborted) break;
        const message = attachmentUploadErrorMessage(err, file.name);
        patchUpload(entry.key, { status: 'error', error: message });
        showToast('error', message);
      }
    }
    if (!mounted.current) return;
    uploadAbort.current = null;
    setUploading(false);
    if (succeeded > 0) {
      setUploadDescription('');
      showToast('success', succeeded === 1 ? `Uploaded ${lastUploaded}.` : `Uploaded ${succeeded} files.`);
      // Newest first: the new files are on page 1
      if (page !== 1) setPage(1); else reload();
    }
  };

  const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Copy the files before clearing the input (which empties its FileList)
    const files = event.target.files ? Array.from(event.target.files) : [];
    void startUploads(files);
    // Let the same file be picked again after a failure
    event.target.value = '';
  };

  const dismissUpload = (key: string) => setUploads((current) => current.filter((entry) => entry.key !== key));

  const openPreview = async (attachment: types.Attachment) => {
    const seq = ++previewSeq.current;
    setPreview((current) => {
      releaseUrl(current?.url);
      return { attachment, url: null };
    });
    try {
      const blob = await api.downloadAttachment(attachment.id);
      if (seq !== previewSeq.current || !mounted.current) return;
      const url = trackUrl(blob);
      setPreview({ attachment, url });
    } catch (err) {
      if (seq !== previewSeq.current || !mounted.current) return;
      setPreview(null);
      showToast('error', `${attachment.filename}: ${getErrorMessage(err, 'Could not load the preview')}`);
      reloadIfGone(err);
    }
  };

  const download = async (attachment: types.Attachment) => {
    if (busyId) return;
    setBusyId(attachment.id);
    try {
      const blob = await api.downloadAttachment(attachment.id, { download: true });
      if (!mounted.current) return;
      const url = trackUrl(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      const timer = setTimeout(() => {
        urlTimers.current.delete(timer);
        releaseUrl(url);
      }, DOWNLOAD_URL_TTL_MS);
      urlTimers.current.add(timer);
    } catch (err) {
      if (!mounted.current) return;
      showToast('error', `${attachment.filename}: ${getErrorMessage(err, 'Download failed')}`);
      reloadIfGone(err);
    } finally {
      if (mounted.current) setBusyId(null);
    }
  };

  const startEdit = (attachment: types.Attachment) => {
    const { base, extension } = splitFilename(attachment.filename);
    setEditing({ id: attachment.id, base, extension, description: attachment.description ?? '' });
  };

  const saveEdit = async (attachment: types.Attachment) => {
    if (!editing || busyId) return;
    const issue = attachmentRenameError(editing.base, editing.extension) ?? attachmentDescriptionError(editing.description);
    if (issue) { showToast('error', issue); return; }
    const filename = `${editing.base.trim()}${editing.extension}`;
    const description = editing.description.trim();
    const data: types.UpdateAttachmentRequest = {};
    if (filename !== attachment.filename) data.filename = filename;
    if (description !== (attachment.description ?? '')) data.description = description || null;
    if (data.filename === undefined && data.description === undefined) { setEditing(null); return; }
    setBusyId(attachment.id);
    try {
      const saved = await api.updateAttachment(attachment.id, data);
      if (!mounted.current) return;
      setAttachments((current) => current?.map((row) => (row.id === saved.id ? saved : row)) ?? current);
      setPreview((current) => (current && current.attachment.id === saved.id ? { ...current, attachment: saved } : current));
      setEditing(null);
      showToast('success', `Saved ${saved.filename}.`);
    } catch (err) {
      if (!mounted.current) return;
      showToast('error', attachmentUpdateErrorMessage(err, editing.extension));
      if (getErrorCode(err) === 'NOT_FOUND') { setEditing(null); reload(); }
    } finally {
      if (mounted.current) setBusyId(null);
    }
  };

  const remove = async (attachment: types.Attachment) => {
    if (busyId) return;
    if (!window.confirm(`Delete "${attachment.filename}"? It will no longer appear on this ${noun}.`)) return;
    setBusyId(attachment.id);
    try {
      await api.deleteAttachment(attachment.id);
      if (!mounted.current) return;
      if (preview?.attachment.id === attachment.id) closePreview();
      if (editing?.id === attachment.id) setEditing(null);
      showToast('success', `Deleted ${attachment.filename}.`);
      // The last row on a later page: step back one page
      if ((attachments?.length ?? 0) <= 1 && page > 1) setPage(page - 1); else reload();
    } catch (err) {
      if (!mounted.current) return;
      showToast('error', `${attachment.filename}: ${getErrorMessage(err, 'Could not delete the attachment')}`);
      reloadIfGone(err);
    } finally {
      if (mounted.current) setBusyId(null);
    }
  };

  const pages = Math.max(1, pagination?.pages ?? 1);
  const total = pagination?.total ?? 0;

  return (
    <section aria-labelledby={`${idBase}-heading`} className={sectionClassName} data-testid="attachments-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 id={`${idBase}-heading`} className={headingClassName}>
          Attachments {attachments && <span className="text-gray-400 font-normal">({total})</span>}
        </h2>
      </div>

      {canUpload && (
        <div className="mb-4 space-y-3">
          <div>
            <label htmlFor={`${idBase}-description`} className={labelClass}>Description for new uploads (optional)</label>
            <input
              id={`${idBase}-description`}
              type="text"
              className={inputClass}
              value={uploadDescription}
              maxLength={ATTACHMENT_DESCRIPTION_MAX}
              disabled={uploading}
              placeholder="e.g. Signed contract, before photo"
              onChange={(event) => setUploadDescription(event.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <label
              className={`${primaryButtonClass} inline-flex items-center justify-center cursor-pointer text-center${uploading ? ' pointer-events-none opacity-60' : ''}`}
            >
              {uploading ? 'Uploading...' : 'Upload files'}
              <input
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="sr-only"
                disabled={uploading}
                data-testid="attachments-file-input"
                onChange={onPick}
              />
            </label>
            {/* Phones: straight to the camera (item 42); desktop browsers would only show a file picker */}
            <label
              className={`${secondaryButtonClass} sm:hidden inline-flex items-center justify-center cursor-pointer text-center${uploading ? ' pointer-events-none opacity-60' : ''}`}
            >
              Take photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                disabled={uploading}
                data-testid="attachments-camera-input"
                onChange={onPick}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Photos (JPEG, PNG, WebP, HEIC), PDF, text, CSV, Word, Excel, RTF and OpenDocument files, up to {ATTACHMENT_MAX_MB} MB each.
          </p>
          {uploads.length > 0 && (
            <ul aria-label="Uploads" className="space-y-2">
              {uploads.map((entry) => (
                <li
                  key={entry.key}
                  data-testid="attachment-upload"
                  data-status={entry.status}
                  className={`rounded-md border p-3 text-sm ${entry.status === 'error' ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words font-medium">{entry.name}</span>
                    <span className="shrink-0 text-gray-600">
                      {entry.status === 'pending' && 'Waiting'}
                      {entry.status === 'uploading' && `${Math.round(entry.progress * 100)}%`}
                      {entry.status === 'done' && 'Uploaded'}
                      {entry.status === 'error' && (
                        <button type="button" className={rowActionClass} aria-label={`Dismiss ${entry.name}`} onClick={() => dismissUpload(entry.key)}>
                          Dismiss
                        </button>
                      )}
                    </span>
                  </div>
                  {(entry.status === 'uploading' || entry.status === 'pending') && (
                    <progress className="mt-2 w-full" max={1} value={entry.progress} aria-label={`Upload progress for ${entry.name}`} />
                  )}
                  {entry.error && <p role="alert" className="mt-1 text-red-800 break-words">{entry.error}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loadError ? (
        <div className="text-sm text-gray-600">
          <p role="alert">Attachments could not be loaded: {loadError}</p>
          <button type="button" className={`${secondaryButtonClass} mt-2`} onClick={reload}>Try again</button>
        </div>
      ) : attachments === null ? (
        <p role="status" className="text-gray-500 text-sm">Loading attachments...</p>
      ) : attachments.length === 0 ? (
        <p className="text-gray-500 text-sm">No attachments on this {noun} yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200" aria-label="Attachments">
          {attachments.map((attachment) => {
            const kind = attachmentKind(attachment.contentType, attachment.filename);
            const isEditing = editing?.id === attachment.id;
            const rowBusy = busyId === attachment.id;
            return (
              <li key={attachment.id} className="py-3" data-testid={`attachment-${attachment.id}`}>
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-xs font-bold ${KIND_STYLES[kind] ?? KIND_STYLES.FILE}`}
                    title={attachment.contentType}
                    data-testid="attachment-kind"
                  >
                    {kind}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 break-words" data-testid="attachment-filename">{attachment.filename}</p>
                    <p className="text-xs text-gray-600 break-words">
                      {formatFileSize(attachment.sizeBytes)} · {attachment.uploadedByEmail} · {formatAttachmentDate(attachment.createdAt)}
                    </p>
                    {attachment.description && !isEditing && (
                      <p className="mt-1 text-sm text-gray-700 break-words whitespace-pre-line" data-testid="attachment-description">
                        {attachment.description}
                      </p>
                    )}
                    {!isEditing && (
                      <div className="mt-1 flex flex-wrap gap-x-4">
                        {attachment.inline && (
                          <button type="button" className={rowActionClass} disabled={rowBusy} aria-label={`Preview ${attachment.filename}`} onClick={() => void openPreview(attachment)}>
                            Preview
                          </button>
                        )}
                        <button type="button" className={rowActionClass} disabled={rowBusy} aria-label={`Download ${attachment.filename}`} onClick={() => void download(attachment)}>
                          {rowBusy ? 'Working...' : 'Download'}
                        </button>
                        {canManage && (
                          <>
                            <button type="button" className={rowActionClass} disabled={!!busyId} aria-label={`Edit ${attachment.filename}`} onClick={() => startEdit(attachment)}>
                              Edit
                            </button>
                            <button type="button" className={rowDangerActionClass} disabled={!!busyId} aria-label={`Delete ${attachment.filename}`} onClick={() => void remove(attachment)}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {isEditing && editing && (
                      <form
                        className="mt-3 space-y-3"
                        aria-label={`Edit ${attachment.filename}`}
                        onSubmit={(event) => { event.preventDefault(); void saveEdit(attachment); }}
                      >
                        <div>
                          <label htmlFor={`${idBase}-filename-${attachment.id}`} className={labelClass}>Filename</label>
                          <div className="flex items-center gap-2">
                            <input
                              id={`${idBase}-filename-${attachment.id}`}
                              type="text"
                              className={inputClass}
                              value={editing.base}
                              disabled={rowBusy}
                              onChange={(event) => setEditing({ ...editing, base: event.target.value })}
                            />
                            {editing.extension && (
                              <span className="shrink-0 text-gray-700" data-testid="attachment-extension">{editing.extension}</span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">The file type ({editing.extension || 'none'}) cannot be changed.</p>
                        </div>
                        <div>
                          <label htmlFor={`${idBase}-edit-description-${attachment.id}`} className={labelClass}>Description</label>
                          <textarea
                            id={`${idBase}-edit-description-${attachment.id}`}
                            className={inputClass}
                            rows={2}
                            maxLength={ATTACHMENT_DESCRIPTION_MAX}
                            value={editing.description}
                            disabled={rowBusy}
                            onChange={(event) => setEditing({ ...editing, description: event.target.value })}
                          />
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <button type="submit" className={primaryButtonClass} disabled={rowBusy}>{rowBusy ? 'Saving...' : 'Save'}</button>
                          <button type="button" className={secondaryButtonClass} disabled={rowBusy} onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pages > 1 && !loadError && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(event) => { if (event.target === event.currentTarget) closePreview(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Preview of ${preview.attachment.filename}`}
            className="flex max-h-full w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
          >
            <div className="flex items-center justify-between gap-3 border-b p-3">
              <p className="min-w-0 break-words font-medium">{preview.attachment.filename}</p>
              <div className="flex shrink-0 gap-3">
                <button type="button" className={rowActionClass} onClick={() => void download(preview.attachment)}>Download</button>
                <button type="button" className={rowActionClass} onClick={closePreview} autoFocus>Close</button>
              </div>
            </div>
            <div className="flex min-h-[12rem] flex-1 items-center justify-center overflow-auto p-3">
              {preview.url ? (
                <img src={preview.url} alt={preview.attachment.description || preview.attachment.filename} className="max-h-[75vh] max-w-full object-contain" />
              ) : (
                <p role="status" className="text-sm text-gray-500">Loading preview...</p>
              )}
            </div>
            {preview.attachment.description && (
              <p className="border-t p-3 text-sm text-gray-700 break-words whitespace-pre-line">{preview.attachment.description}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
